import { useEffect, useRef, useState } from "react";
import { ADMINS_MAX, addedByWords } from "../../../../shared/admins";
import { NO_ROW, OWNER, triesWords, type PinRow } from "../../../../shared/pins";
import { ConfirmButton, clockTime, type TabProps } from "./common";
import { LinkRow } from "./Links";
import { NAME_MAX, cleanName } from "./who";

/** The Admins fold's closed line: who else can run the day. */
export function adminsSummary(count: number, named: boolean): string {
  const others = `${count} ${count === 1 ? "admin" : "admins"}`;
  if (named) return `The owner and ${others}`;
  return count ? `You and ${others}` : "Only you";
}

/** An admin's PIN on their row: whether they have one, and any wrong tries. */
function pinWords(row: PinRow, now: number): string {
  const tries = triesWords(row, now, clockTime);
  return `${row.set ? "PIN set" : "No PIN yet"}${tries ? `. ${tries}` : ""}`;
}

/**
 * Admins by name (decision 38): type a name, and that person has an admin link of their own, with
 * its code open to scan there and then. What they do is logged under their name, and taking their
 * link away stops only their phone. Every admin can add, replace and take away named admins; only
 * the owner's screen holds the owner's link.
 */
export function AdminsSection({ t, say, me }: Pick<TabProps, "t" | "say" | "me">) {
  const admins = t.links?.admins ?? [];
  const [typed, setTyped] = useState("");
  const clean = cleanName(typed);
  // the name just asked for, and who was on the list then: the new row is the one that was not
  const asked = useRef<{ name: string; before: Set<string> } | null>(null);
  // the row whose code is open because this phone just made its link
  const [fresh, setFresh] = useState<string | null>(null);
  const full = admins.length >= ADMINS_MAX;
  const book = t.pins;
  const now = t.now();
  const paused = book?.pickPausedUntil && book.pickPausedUntil > now ? book.pickPausedUntil : null;
  const ownPin = book?.admins[OWNER] ?? NO_ROW;

  /** Reset an admin's PIN (decision 40): their phones ask for a new one on the next open of their link. */
  function resetPin(id: string, whose: string) {
    void t.resetPin("admin", id).then((a) => say(a.ok ? `${whose} PIN was reset.` : a.reason ?? "That did not work."));
  }

  useEffect(() => {
    const ask = asked.current;
    if (!ask) return;
    const added = admins.find((a) => !ask.before.has(a.id) && a.name.toLowerCase() === ask.name.toLowerCase());
    if (!added) return;
    asked.current = null;
    setFresh(added.id);
    say(`${added.name} is an admin. Scan the code with their phone.`);
  }, [admins]);

  // a refusal (a name already taken, the list full) means nothing new is coming
  useEffect(() => {
    asked.current = null;
  }, [t.refusal?.at]);

  function add() {
    if (!clean || full) return;
    // the owner's name is kept on this phone only, so the server cannot see two people sharing it
    if (me.name && clean.toLowerCase() === me.name.trim().toLowerCase()) {
      say("That is your name. Give them their own.");
      return;
    }
    asked.current = { name: clean, before: new Set(admins.map((a) => a.id)) };
    t.addAdmin(clean);
    setTyped("");
  }

  return (
    <>
      <p className="ad-note">
        {me.named ? "You are an admin. The owner's link is not shown here." : "You are the owner. Only you can replace your own link."}
      </p>
      {!me.named && (
        <div className="ad-row ad-player-pin">
          <span className="ad-note ad-grow">{ownPin.set ? "Your admin PIN is set" : "You have no admin PIN yet"}{triesWords(ownPin, now, clockTime) ? `. ${triesWords(ownPin, now, clockTime)}` : ""}</span>
          <ConfirmButton className="ad-small" label="Reset my PIN" yes="Reset it"
            question="Reset your admin PIN? Every phone on the owner link, this one too, asks for a new one straight away."
            onConfirm={() => resetPin(OWNER, "Your admin")} />
        </div>
      )}
      {paused && (
        <p className="ad-note"><b>Picking an admin on a player's phone is paused until {clockTime(paused)}: too many wrong admin PINs were typed there.</b></p>
      )}
      <form className="ad-row" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <input value={typed} maxLength={NAME_MAX} placeholder="Their first name" aria-label="New admin's name" autoComplete="off"
          disabled={full} onChange={(e) => setTyped(e.target.value)} />
        <button type="submit" className="btn accent" disabled={!clean || full}>Add admin</button>
      </form>
      <p className="ad-note">
        {full
          ? `That is ${ADMINS_MAX} admins, the most there can be. Take one away to add another.`
          : "They get a link of their own, logged under their name. Taking it away stops only their phone."}
      </p>
      {admins.length > 0 && (
        <ul className="ad-list ad-links">
          {admins.map((a) => {
            const mine = me.named && me.name !== null && a.name.toLowerCase() === me.name.toLowerCase();
            const added = addedByWords(a.addedBy, { owner: !me.named, name: me.name });
            const pin = pinWords(book?.admins[a.id] ?? NO_ROW, now);
            const resetKey = (
              <ConfirmButton className="ad-small" label="Reset PIN" yes="Reset it"
                question={mine ? "Reset your admin PIN? This phone asks for a new one straight away." : `Reset ${a.name}'s admin PIN? Their phone asks for a new one straight away.`}
                onConfirm={() => resetPin(a.id, mine ? "Your admin" : `${a.name}'s admin`)} />
            );
            return (
              <LinkRow key={a.link + (fresh === a.id ? ":fresh" : "")} label={mine ? `${a.name} (you)` : a.name} link={a.link} say={say}
                startOpen={fresh === a.id} note={added ? `${added}. ${pin}` : pin}
                extra={mine ? resetKey : (
                  <>
                    {resetKey}
                    <ConfirmButton className="ad-small" label="New link" yes="Make it"
                      question={`Sure? ${a.name}'s phone stops working until it has the new link, and they set a new PIN.`}
                      onConfirm={() => { setFresh(a.id); t.renewAdmin(a.id); say(`New link made for ${a.name}.`); }} />
                    <ConfirmButton className="ad-small" label="Remove" yes="Remove"
                      question={`Sure? ${a.name} stops being an admin straight away.`}
                      onConfirm={() => { t.removeAdmin(a.id); say(`${a.name} is not an admin any more.`); }} />
                  </>
                )} />
            );
          })}
        </ul>
      )}
    </>
  );
}
